import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Check } from '@untitled-ui/icons-react'
import React, { forwardRef } from 'react'
import {
  composeRenderProps,
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuItemProps,
  MenuProps as AriaMenuProps,
  Separator,
  SeparatorProps
} from 'react-aria-components'
import {
  dropdownItemStyles,
  DropdownSection,
  DropdownSectionProps
} from './ListBox'
import { Popover, PopoverProps } from './Popover'
import SearchField from './SearchField'
import { twMerge } from 'tailwind-merge'

export interface MenuProps<T> extends AriaMenuProps<T> {
  placement?: PopoverProps['placement']
  isNonModal?: boolean
  search?: string
  onSearch?: (search: string) => void
}

export const Menu = forwardRef(function Menu<T extends object>(
  props: MenuProps<T>,
  ref: React.Ref<HTMLDivElement>
) {
  return (
    <Popover
      ref={ref}
      isNonModal={props.isNonModal}
      placement={props.placement}
      className='relative min-w-[150px]'>
      {typeof props.search === 'string' && (
        <span className='flex items-start p-2 w-full gap-1 flex-col'>
          <SearchField
            className={'w-full'}
            value={props.search}
            onChange={props.onSearch}
            placeholder={t`Search`}
          />

          {!props.search && (
            <p className={'text-xs text-neutral-400'}>
              <Trans>Start typing to find results...</Trans>
            </p>
          )}
        </span>
      )}
      <AriaMenu
        {...props}
        className={twMerge(
          'relative w-full flex flex-col outline outline-0 overflow-auto',
          props.className
        )}
      />
    </Popover>
  )
}) as <T extends object>(
  props: MenuProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => React.JSX.Element

export function MenuItem({
  showCheck = true,
  ...props
}: MenuItemProps & { isSubmenu?: boolean; showCheck?: boolean }) {
  const textValue =
    props.textValue ??
    (typeof props.children === 'string' ? props.children : undefined)
  return (
    <AriaMenuItem
      {...props}
      textValue={textValue}
      className={dropdownItemStyles}>
      {composeRenderProps(props.children, children => (
        <>
          {children}
          {!props.isSubmenu && showCheck && (
            <Check
              width={18}
              className='text-brand-600 opacity-0 group-selected:opacity-100'
            />
          )}
        </>
      ))}
    </AriaMenuItem>
  )
}

export function MenuSeparator(props: SeparatorProps) {
  return <Separator {...props} className='border-b border-neutral-300' />
}

export function MenuSection<T extends object>(props: DropdownSectionProps<T>) {
  return <DropdownSection {...props} />
}
